import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useI18n } from '@/hooks/useI18n';
import { useChartTooltipTrigger } from '@/hooks/useChartTooltipTrigger';
import { formatInt, formatNumber } from '@/lib/format';
import type { ConversionsResponse } from '@/types/api';

export function DestinationClickChart({ data }: { data: ConversionsResponse }) {
  const { t, locale } = useI18n();
  const trigger = useChartTooltipTrigger();
  const measuredPoints = data.clickSeries.filter((point) => point.clicks !== null).length;
  const dateLabel = (value: string) =>
    new Date(`${value.slice(0, 10)}T00:00:00Z`).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
  const tick = { fill: 'hsl(var(--text-3))', fontSize: 10 };
  return (
    <figure
      aria-labelledby="destination-click-trend-title"
      className="min-w-0 rounded-[14px] border border-emerald/20 bg-bg-1 p-4 sm:p-5"
    >
      <figcaption>
        <h3 id="destination-click-trend-title" className="text-[14px] font-semibold text-text-1">
          {t('Évolution des clics vers la destination', 'Destination clicks over time')}
        </h3>
        <p className="mt-1 text-[11px] leading-5 text-text-3">
          {t(
            'Tous les liens rapprochés, comptés une seule fois · journées UTC terminées.',
            'All matched links, counted once · completed UTC days.',
          )}
        </p>
        <p className="mt-3 text-[24px] font-semibold tabular-nums text-emerald">
          {formatInt(data.summary.clicks)}{' '}
          <span className="text-[12px] font-normal text-text-3">
            {t('clics sur la période', 'clicks over the period')}
          </span>
        </p>
      </figcaption>
      {measuredPoints > 0 ? (
        <div
          className="mt-3 h-[240px] min-w-0"
          role="img"
          aria-label={t(
            'Clics destination de tous les liens rapprochés, par jour UTC',
            'Destination clicks for all matched links, per UTC day',
          )}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data.clickSeries}
              margin={{ top: 8, right: 12, left: -8, bottom: 0 }}
              accessibilityLayer
            >
              <CartesianGrid vertical={false} stroke="hsl(var(--line-1))" />
              <XAxis
                dataKey="date"
                tickFormatter={dateLabel}
                tick={tick}
                tickLine={false}
                axisLine={false}
                minTickGap={35}
              />
              <YAxis
                tickFormatter={formatNumber}
                tick={tick}
                tickLine={false}
                axisLine={false}
                width={50}
              />
              <Tooltip
                trigger={trigger}
                labelFormatter={(value) => `${dateLabel(String(value))} · UTC`}
                formatter={(value: number) => formatInt(value)}
                filterNull={false}
              />
              <Line
                dataKey="clicks"
                name={t('Clics vers la destination', 'Destination clicks')}
                stroke="hsl(var(--emerald))"
                strokeWidth={2.5}
                dot={measuredPoints === 1 ? { r: 3 } : false}
                connectNulls={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="py-10 text-center text-[13px] text-text-3">
          {t(
            'Aucun historique de clics mesuré disponible.',
            'No measured click history available.',
          )}
        </p>
      )}
      <p className="mt-2 text-[11px] leading-5 text-text-3">
        {t(
          'Cet historique reste disponible quand les vues ne peuvent pas encore être rapprochées. Les interruptions indiquent des données manquantes.',
          'This history remains available when views cannot yet be matched. Gaps indicate missing data.',
        )}
      </p>
    </figure>
  );
}
